import { useCallback, useEffect, useState, useReducer } from "react";
import { useTranslation } from "react-i18next";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import styles from "./Categories.module.scss";
import DeleteModal from "../../components/common/delete-modal/DeleteModal";
import { useAuth } from "../../context/AuthContext";
import Modal from "../../components/common/modal/Modal";
import Input from "../../components/common/input/Input";
import Button from "../../components/common/button/Button";
import PlusIcon from "../../assets/images/actions/PlusIcon";
import CategorisService from "../../api/services/categories.service";
import AttributesService from "../../api/services/attributes.service";
import ShowImage from "../../components/common/show-image/ShowImage";
import NestedDataTable from "../../components/common/nested-data-table/NestedDataTable";
import { Column } from "../../components/common/data-table/DataTable";
import AttributesModal from "./AttributesModal";

interface Errors {
  name_en: string;
  name_az: string;
  name_es: string;
  name_de: string;
  image: string;
}

type Language = "en" | "az" | "es" | "de";

interface CategoryWithTranslations {
  id: number;
  name: string;
  name_en?: string;
  name_az?: string;
  name_es?: string;
  name_de?: string;
  image?: string;
  parent_id?: number | null;
  children?: CategoryWithTranslations[];
  attributes?: any[];
}

interface FormState {
  name_en: string;
  name_az: string;
  name_es: string;
  name_de: string;
  image: File | string | null;
  preview: string;
}

type FormAction =
  | { type: "SET_FIELD"; field: keyof FormState; value: any }
  | { type: "RESET" }
  | { type: "LOAD_DATA"; data: Partial<FormState> };

const initialFormState: FormState = {
  name_en: "",
  name_az: "",
  name_es: "",
  name_de: "",
  image: null,
  preview: "",
};

const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "RESET":
      // Clean up any blob URLs before reset
      if (state.preview && state.preview.startsWith("blob:")) {
        URL.revokeObjectURL(state.preview);
      }
      return initialFormState;
    case "LOAD_DATA":
      return { ...state, ...action.data };
    default:
      return state;
  }
};

const Categories = () => {
  const { t } = useTranslation("translation");
  const [categories, setCategories] = useState<CategoryWithTranslations[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [activeLanguage, setActiveLanguage] = useState<Language>("en");
  const [formData, dispatch] = useReducer(formReducer, initialFormState);

  // Error and success states
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  // Attributes Modal State
  const [attributesModalOpen, setAttributesModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [editAttributeId, setEditAttributeId] = useState<number | null>(null);

  // Attributes cache (in-memory only, no localStorage)
  const [categoryAttributesCache, setCategoryAttributesCache] = useState<
    Record<number, any[]>
  >({});

  // Dropdown state
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);

  const { setButtons, searchValue } = useAuth();
  const [errors, setErrors] = useState<Partial<Errors>>({});
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [isSubcategoryModal, setIsSubcategoryModal] = useState(false);
  const [parentCategory, setParentCategory] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const languages = [
    { code: "en" as Language, label: "English", flag: "🇬🇧" },
    { code: "az" as Language, label: "Azərbaycanca", flag: "🇦🇿" },
    { code: "es" as Language, label: "Español", flag: "🇪🇸" },
    { code: "de" as Language, label: "Deutsch", flag: "🇩🇪" },
  ];

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (formData.preview && formData.preview.startsWith("blob:")) {
        URL.revokeObjectURL(formData.preview);
      }
    };
  }, [formData.preview]);

  const getData = useCallback(async () => {
    if (!searchValue) setLoading(true);
    setErrorMessage("");

    try {
      const res = await CategorisService.getAllCategories(searchValue);
      const categoriesData = res.data;

      // Fetch all attributes with pagination
      let allAttributesData: any[] = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        try {
          const attributesRes = await AttributesService.getAllData(
            currentPage,
            100,
            "",
          );
          const pageData = attributesRes.data.data || [];
          allAttributesData = [...allAttributesData, ...pageData];

          if (pageData.length < 100) {
            hasMore = false;
          } else {
            currentPage++;
          }
        } catch (error) {
          console.error("Error fetching attributes page:", currentPage, error);
          hasMore = false;
        }
      }

      // Enrich categories with their attributes
      const enrichedCategories = categoriesData.map((cat: any) => {
        const categoryAttrs = allAttributesData.filter(
          (attr: any) => attr.category_id === cat.id,
        );
        return {
          ...cat,
          attributes: categoryAttrs,
        };
      });

      setCategories(enrichedCategories);

      // Update in-memory cache
      const newCache: Record<number, any[]> = {};
      enrichedCategories.forEach((cat: any) => {
        newCache[cat.id] = cat.attributes || [];
      });
      setCategoryAttributesCache(newCache);
    } catch (error: any) {
      console.error(error);
      setErrorMessage(
        error?.response?.data?.message ||
          (t("categories.errors.failed_to_load") as string),
      );
    } finally {
      setLoading(false);
    }
  }, [searchValue]);

  const handleAddAttributes = (categoryId: number, categoryName: string) => {
    setSelectedCategory({ id: categoryId, name: categoryName });
    setEditAttributeId(null);
    setAttributesModalOpen(true);
  };

  const handleEditAttribute = (
    attributeId: number,
    categoryId: number,
    categoryName: string,
  ) => {
    setSelectedCategory({ id: categoryId, name: categoryName });
    setEditAttributeId(attributeId);
    setAttributesModalOpen(true);
  };

  const handleAttributesModalClose = () => {
    setAttributesModalOpen(false);
    setSelectedCategory(null);
    setEditAttributeId(null);
    getData();
  };

  const handleAddSubcategory = (parentId: number, parentName: string) => {
    setParentCategory({ id: parentId, name: parentName });
    setIsSubcategoryModal(true);
    setEditId(null);
    setActiveLanguage("en");
    dispatch({ type: "RESET" });
    setErrors({});
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleSubcategorySubmit = async () => {
    if (!validateForm() || !parentCategory) return;

    setErrorMessage("");
    setSuccessMessage("");

    try {
      const data = new FormData();
      if (formData.name_en) data.append("name_en", formData.name_en);
      if (formData.name_az) data.append("name_az", formData.name_az);
      if (formData.name_es) data.append("name_es", formData.name_es);
      if (formData.name_de) data.append("name_de", formData.name_de);
      if (formData.image instanceof File) data.append("image", formData.image);
      data.append("parent_id", parentCategory.id.toString());

      await CategorisService.createCategory(data);

      setSuccessMessage(
        t("categories.messages.subcategory_created_successfully") as string,
      );

      setTimeout(() => {
        setIsSubcategoryModal(false);
        setParentCategory(null);
        setActiveLanguage("en");
        dispatch({ type: "RESET" });
        getData();
      }, 1000);
    } catch (error: any) {
      console.error(error);
      setErrorMessage(
        error?.response?.data?.message ||
          (t("categories.errors.failed_to_create_subcategory") as string),
      );
    }
  };

  const handleDelete = async (id: number) => {
    setErrorMessage("");

    try {
      await CategorisService.deleteCategory(id);

      // Clear from in-memory cache
      setCategoryAttributesCache((prev) => {
        const newCache = { ...prev };
        delete newCache[id];
        return newCache;
      });

      setDeleteModal(false);
      setSuccessMessage(
        t("categories.messages.deleted_successfully") as string,
      );

      setTimeout(() => {
        setSuccessMessage("");
        getData();
      }, 1500);
    } catch (error: any) {
      console.error(error);
      setErrorMessage(
        error?.response?.data?.message ||
          (t("categories.errors.failed_to_delete") as string),
      );
      setDeleteModal(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Errors> = {};

    if (!formData.name_en.trim()) {
      newErrors.name_en = t(
        "categories.errors.english_name_required",
      ) as string;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setErrorMessage("");
    setSuccessMessage("");

    try {
      const data = new FormData();
      if (formData.name_en) data.append("name_en", formData.name_en);
      if (formData.name_az) data.append("name_az", formData.name_az);
      if (formData.name_es) data.append("name_es", formData.name_es);
      if (formData.name_de) data.append("name_de", formData.name_de);
      if (formData.image instanceof File) data.append("image", formData.image);

      if (editId) {
        await CategorisService.updateCategory(editId, data);
        setSuccessMessage(
          t("categories.messages.updated_successfully") as string,
        );
      } else {
        await CategorisService.createCategory(data);
        setSuccessMessage(
          t("categories.messages.created_successfully") as string,
        );
      }

      setTimeout(() => {
        setIsVisible(false);
        setActiveLanguage("en");
        dispatch({ type: "RESET" });
        setEditId(null);
        getData();
      }, 1000);
    } catch (error: any) {
      console.error(error);
      setErrorMessage(
        error?.response?.data?.message ||
          (t("categories.errors.failed_to_save") as string),
      );
    }
  };

  const handleEdit = async (id: number) => {
    setErrors({});
    setErrorMessage("");

    try {
      const res = await CategorisService.getSingleCategory(id);

      setEditId(id);
      const categoryData = res.data.name ? res.data : res.data[0];

      dispatch({
        type: "LOAD_DATA",
        data: {
          name_en: categoryData.name_en || categoryData.name || "",
          name_az: categoryData.name_az || "",
          name_es: categoryData.name_es || "",
          name_de: categoryData.name_de || "",
          image: categoryData.image || "",
          preview: categoryData.image || "",
        },
      });

      setActiveLanguage("en");
      setIsVisible(true);
    } catch (error: any) {
      console.error(error);
      setErrorMessage(
        error?.response?.data?.message ||
          (t("categories.errors.failed_to_load_data") as string),
      );
    }
  };

  useEffect(() => {
    getData();
  }, [getData]);

  const toggleDropdown = (categoryId: number) => {
    setOpenDropdownId((prev) => (prev === categoryId ? null : categoryId));
  };

  const columns: Column[] = [
    { key: "name", header: t("categories.table.category_name") as string },
    {
      key: "image",
      header: t("categories.table.image") as string,
      render: (i) => (
        <div
          className={styles.brand_img_wrapper}
          onClick={() => {
            setSelectedImage(i);
            setShowImageModal(true);
          }}
        >
          {i ? <img src={i} alt="brand" className={styles.brand_img} /> : "-"}
        </div>
      ),
    },
    {
      key: "attributes",
      header: t("categories.table.attributes") as string,
      render: (attrs: any[], row: any) => {
        const categoryAttrs = categoryAttributesCache[row.id] || attrs || [];
        const isOpen = openDropdownId === row.id;

        if (!categoryAttrs || categoryAttrs.length === 0) {
          return (
            <span className={styles.no_attributes}>
              {t("categories.attributes.no_attributes") as string}
            </span>
          );
        }

        return (
          <div className={styles.attributes_dropdown}>
            <div
              className={`${styles.dropdown_trigger} ${
                isOpen ? styles.open : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdown(row.id);
              }}
            >
              <div className={styles.trigger_content}>
                <svg
                  className={styles.attributes_icon}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3 6h18M3 12h18M3 18h18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <span className={styles.attributes_count}>
                  {categoryAttrs.length}{" "}
                  {categoryAttrs.length === 1
                    ? (t("categories.attributes.attribute") as string)
                    : (t("categories.attributes.attributes") as string)}
                </span>
              </div>
              <svg
                className={`${styles.dropdown_arrow} ${
                  isOpen ? styles.rotated : ""
                }`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
              >
                <path
                  d="M2 4l4 4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {isOpen && (
              <div className={styles.dropdown_content}>
                <div className={styles.dropdown_header}>
                  <span className={styles.header_title}>
                    {t("categories.attributes.attribute_details") as string}
                  </span>
                </div>
                <div className={styles.attributes_list}>
                  {categoryAttrs.map((attr: any, idx: number) => (
                    <div key={idx} className={styles.attribute_item}>
                      <div className={styles.attribute_header}>
                        <div className={styles.attribute_icon_wrapper}>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="3"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M12 2v4m0 12v4M2 12h4m12 0h4"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                        <span className={styles.attribute_name}>
                          {attr.name || attr.name_en}
                        </span>
                        <button
                          className={styles.edit_attribute_btn}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAttribute(attr.id, row.id, row.name);
                          }}
                          title={
                            t("categories.attributes.edit_attribute") as string
                          }
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                      {attr.values && attr.values.length > 0 && (
                        <div className={styles.attribute_values}>
                          <span className={styles.values_label}>
                            {t("categories.attributes.values") as string}
                          </span>
                          <div className={styles.values_chips}>
                            {attr.values
                              .slice(0, 3)
                              .map((val: any, vidx: number) => (
                                <span key={vidx} className={styles.value_chip}>
                                  {val.name || val.name_en}
                                </span>
                              ))}
                            {attr.values.length > 3 && (
                              <span className={styles.value_chip_more}>
                                +{attr.values.length - 3}{" "}
                                {t("categories.attributes.more") as string}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      <div className={styles.attribute_meta}>
                        {attr.is_filter && (
                          <span className={styles.meta_badge}>
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M3 4h18v2l-7 7v6l-4 2v-8L3 6V4z" />
                            </svg>
                            {t("categories.attributes.filter") as string}
                          </span>
                        )}
                        {attr.is_view && (
                          <span className={styles.meta_badge}>
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                            </svg>
                            {t("categories.attributes.changeable") as string}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    setButtons([
      <Button
        icon={<PlusIcon />}
        key="add"
        onClick={() => {
          setIsVisible(true);
          setActiveLanguage("en");
          setErrors({});
          setErrorMessage("");
          setSuccessMessage("");
        }}
      >
        {t("categories.add_button") as string}
      </Button>,
    ]);
    return () => setButtons([]);
  }, [setButtons]);

  const renderLanguageTabs = () => (
    <div className={styles.languageTabs}>
      {languages.map((lang) => (
        <button
          key={lang.code}
          type="button"
          className={`${styles.languageTab} ${
            activeLanguage === lang.code ? styles.active : ""
          }`}
          onClick={() => setActiveLanguage(lang.code)}
        >
          <span className={styles.flag}>{lang.flag}</span>
          <span className={styles.langLabel}>{lang.label}</span>
          {lang.code === "en" && <span className={styles.required}>*</span>}
        </button>
      ))}
    </div>
  );

  const renderModalContent = () => (
    <form className={styles.form}>
      {/* Success/Error Messages */}
      {successMessage && (
        <div className={styles.successBanner}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className={styles.errorBanner}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{errorMessage}</span>
        </div>
      )}

      {renderLanguageTabs()}

      <div className={styles.languageContent}>
        {activeLanguage === "en" && (
          <Input
            label={t("categories.form.name_english") as string}
            placeholder={t("categories.form.placeholder_english") as string}
            value={formData.name_en}
            onChange={(e) => {
              dispatch({
                type: "SET_FIELD",
                field: "name_en",
                value: e.target.value,
              });
              setErrors((prev) => ({ ...prev, name_en: "" }));
            }}
            error={errors.name_en}
          />
        )}

        {activeLanguage === "az" && (
          <Input
            label={t("categories.form.name_azerbaijani") as string}
            placeholder={t("categories.form.placeholder_azerbaijani") as string}
            value={formData.name_az}
            onChange={(e) => {
              dispatch({
                type: "SET_FIELD",
                field: "name_az",
                value: e.target.value,
              });
              setErrors((prev) => ({ ...prev, name_az: "" }));
            }}
            error={errors.name_az}
          />
        )}

        {activeLanguage === "es" && (
          <Input
            label={t("categories.form.name_spanish") as string}
            placeholder={t("categories.form.placeholder_spanish") as string}
            value={formData.name_es}
            onChange={(e) => {
              dispatch({
                type: "SET_FIELD",
                field: "name_es",
                value: e.target.value,
              });
              setErrors((prev) => ({ ...prev, name_es: "" }));
            }}
            error={errors.name_es}
          />
        )}

        {activeLanguage === "de" && (
          <Input
            label={t("categories.form.name_german") as string}
            placeholder={t("categories.form.placeholder_german") as string}
            value={formData.name_de}
            onChange={(e) => {
              dispatch({
                type: "SET_FIELD",
                field: "name_de",
                value: e.target.value,
              });
              setErrors((prev) => ({ ...prev, name_de: "" }));
            }}
            error={errors.name_de}
          />
        )}
      </div>

      <div className={styles.imageInputWrapper}>
        <Input
          label={t("categories.form.image") as string}
          type="file"
          onChange={(e) => {
            const file = e.target.files?.[0] || null;

            // Clean up previous blob URL
            if (formData.preview && formData.preview.startsWith("blob:")) {
              URL.revokeObjectURL(formData.preview);
            }

            dispatch({
              type: "LOAD_DATA",
              data: {
                image: file,
                preview: file ? URL.createObjectURL(file) : formData.preview,
              },
            });
            setErrors((prev) => ({ ...prev, image: "" }));
          }}
          error={errors.image}
        />
        {formData.preview && (
          <div className={styles.previewWrapper}>
            <img
              src={formData.preview}
              alt="preview"
              className={styles.previewImage}
            />
          </div>
        )}
      </div>
    </form>
  );

  return (
    <div className={styles.delivery}>
      <TitleUpdater title={t("categories.title") as string} />

      {/* Global Success/Error Messages */}
      {successMessage && !isVisible && !isSubcategoryModal && (
        <div className={styles.globalSuccessBanner}>{successMessage}</div>
      )}

      {errorMessage && !isVisible && !isSubcategoryModal && (
        <div className={styles.globalErrorBanner}>{errorMessage}</div>
      )}

      <NestedDataTable
        table_title={t("categories.title") as string}
        data={categories}
        columns={columns}
        actions
        onDelete={(id) => {
          setDeleteModal(true);
          setDeleteId(id);
        }}
        onEdit={(id) => handleEdit(id)}
        onAddSubcategory={handleAddSubcategory}
        onAddAttributes={handleAddAttributes}
        other
        loading={loading}
      />

      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={() => handleDelete(Number(deleteId))}
        title={t("categories.delete_title") as string}
      />

      <Modal
        isOpen={isVisible}
        onClose={() => {
          setIsVisible(false);
          setEditId(null);
          setActiveLanguage("en");
          dispatch({ type: "RESET" });
          setErrorMessage("");
          setSuccessMessage("");
        }}
        title={
          editId
            ? (t("categories.edit_title") as string)
            : (t("categories.add_button") as string)
        }
        onSubmit={handleSubmit}
      >
        {renderModalContent()}
      </Modal>

      <Modal
        isOpen={isSubcategoryModal}
        onClose={() => {
          setIsSubcategoryModal(false);
          setParentCategory(null);
          setActiveLanguage("en");
          dispatch({ type: "RESET" });
          setErrorMessage("");
          setSuccessMessage("");
        }}
        title={`${t("categories.add_subcategory_title") as string} ${parentCategory?.name || "Parent"}`}
        onSubmit={handleSubcategorySubmit}
      >
        {renderModalContent()}
      </Modal>

      <AttributesModal
        isOpen={attributesModalOpen}
        onClose={handleAttributesModalClose}
        categoryId={selectedCategory?.id || null}
        categoryName={selectedCategory?.name || ""}
        editAttributeId={editAttributeId}
        onSuccess={getData}
      />

      <ShowImage
        imagesModal={showImageModal}
        setImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default Categories;
